// Centrale konstanter for simulering
export const CONSTANTS = {
  // Priser
  TSO_AFGIFT: 135, // DKK/MWh for grid electricity
  INITIAL_INVENTORY_PRICE: 200, // DKK/MWh
  FACTORY_WASTE_HEAT_PRICE: 150, // DKK/MWh fast pris
  OPEX_COST_PER_MWH: 79, // DKK/MWh
  
  // Efficiencies
  EFFICIENCY: {
    heater_to_htes: 0.95,
    heater_to_ltes: 0.95,
    htes_discharge: 0.95,
    ltes_discharge: 1.00,   // NO LOSS - fjernet 5% discharge loss per bruger anmodning
    ltes_charge: 0.95,
    turbine_heat: 0.85,    // 85% bliver til varme
    turbine_electric: 0.15, // 15% bliver til el
    battery_charge: 0.95,    // 95% charge efficiency
    battery_discharge: 0.95, // 95% discharge efficiency
    battery_roundtrip: 0.9025, // 95% * 95% = 90.25% round-trip
    production_loss: 0.03,  // 3% produktionstab
    grid_loss: 0.10         // 10% gridloss
  },
  
  // Storage reserves - OPTIMIZED for maximum cheap energy acceptance
  STORAGE_RESERVES: {
    ltes_min: 0.02,  // AGGRESSIVE: 2% min - almost empty allowed
    htes_min: 0.02,  // AGGRESSIVE: 2% min - maximize turbine runtime
    ltes_max: 0.995, // 99.5% - absolute maximum for waste heat
    htes_max: 0.99,  // 99% - maximize HTES charging capacity
    battery_max: 1.00 // 100% maximum for battery (for daily cycles)
  },
  
  // Time windows
  TIME_WINDOWS: {
    arbitrage_charge: { start: 10, end: 14 },
    arbitrage_discharge: { start: 17, end: 21 }
    
  },
  
  // Price thresholds - ULTRA AGGRESSIVE for cheap sources
  PRICE_THRESHOLDS: {
    very_cheap: 50,     // ULTRA LOW - max charging below this
    cheap: 150,         // Below waste heat price - always charge
    expensive: 250,     // Run turbine aggressively above this
    very_expensive: 350 // Always run turbine at max above this
  },
  
  // Annual demand targets
  ANNUAL_DEMAND: {
    anPlant: 47621,
    abPlant: 46192,
    netDelivery: 41573
  },
  
  // Simulation parameters
  SIMULATION: {
    totalHours: 8784,  // Korrekt antal timer i 2024 (366 dage * 24 timer)
    startDate: new Date('2024-01-01T00:00:00'),
    endDate: new Date('2024-12-31T23:00:00')
  },
  
  // OPEX - Årlige driftsomkostninger (DKK)
  OPEX: {
    forsyningDanmarkLicens: 1465154,    // Forsyning Danmark licens/Salg af ydelser
    it: 250000,                         // IT omkostninger
    revision: 250000,                   // Revision
    administration: 562205,             // Administration
    driftVedligehold: 824568,          // D&V (Drift & Vedligehold)
    total: 3351927                      // Total OPEX
  },
  
  // Battery system services (capacity revenue) - conservative DK2 defaults
  BATTERY_SERVICES: {
    ratePerMWPerHour: 125, // DKK/MW/h (konservativt gennemsnit, kan konfigureres)
    minSocFraction: 0.10,  // min. SOC for at kunne levere (forenklet antagelse)
    requireIdle: true      // kun når batteriet ikke charger/discharger
  },
  
  // Turbine system services (availability revenue) - conservative DK2-style defaults
  TURBINE_SERVICES: {
    ratePerMWPerHour: 100 // DKK/MW/h (kan konfigureres)
  },
  
  // Battery CAPEX default for ROI (kan overskrives i UI config)
  BATTERY_CAPEX_DEFAULT: 6500000,
  
  // CAPEX - Engangsinvesteringer (DKK)
  CAPEX: {
    elKedel: {
      '10MW': 10000000,
      '30MW': 30000000,
      '45MW': 45000000,
      '60MW': 60000000
    },
    tilslutning: 9000000,               // Tilslutning til elnet
    bygninger: 3150000,                 // Bygninger
    hojTemperaturTank: 40000000,        // Høj temperatur tank (Rock store) 150 MWh
    orc: 600000,                        // ORC (Organic Rankine Cycle)
    turbine: 5700000,                   // Turbine 5,5 MW
    akkumuleringstank: 6410000,         // Akkumuleringstank 3100 M3, 205 MWh (LTES)
    solfelt: 0,                          // Solfelt (ikke implementeret endnu)
    batterier: 5000000,                 // Batterier 3 MW / 3 MWh
    rorføring: 0,                        // Rørføring (inkluderet i andre)
    stikledninger: 0,                    // Stikledninger (inkluderet i andre)
    projektledelse: 6000000,             // Projektledelse og myndighedsbehandling
    software: 1500000,                   // Software
    diverse: 4850000,                    // Diverse
    getTotalCapex: function(heaterMW = 10) {
      const heaterKey = `${heaterMW}MW`;
      const heaterCost = this.elKedel[heaterKey] || this.elKedel['10MW'];
      return heaterCost + this.tilslutning + this.bygninger + 
             this.hojTemperaturTank + this.orc + this.turbine + 
             this.akkumuleringstank + this.solfelt + this.batterier + 
             this.rorføring + this.stikledninger + this.projektledelse + 
             this.software + this.diverse;
    }
  }
};

// Season determination
export const getSeason = (month) => {
  if (month >= 10 || month <= 3) return 'WINTER';
  if (month >= 4 && month <= 5) return 'SHOULDER';
  if (month >= 6 && month <= 8) return 'SUMMER';
  return 'SHOULDER';
};

// Demand calculation helpers
// demandMW is the actual customer demand (net level)
// We need to calculate backwards through the losses to find required production
export const calculateDemand = (demandMW) => {
  // demandMW is already at net level (customer demand)
  const netDemand = demandMW;
  
  // Calculate Ab-plant requirement (before 10% grid loss)
  const abDemand = netDemand / 0.90;  // Need to produce more to account for 10% loss
  
  // Calculate An-plant requirement (before 3% production loss)  
  const anDemand = abDemand / 0.97;   // Need to produce more to account for 3% loss
  
  return {
    an: anDemand,      // Required An-plant production
    ab: abDemand,      // Required Ab-plant delivery
    net: netDemand     // Actual customer delivery
  };
};

// Loss calculations
// FIXED: Efficiency means "percentage delivered", not "percentage lost"
// Example: efficiency 0.97 = 97% delivered, 3% lost
export const applyProductionLoss = (amount, efficiency) => {
  const delivered = amount * efficiency;      // 97% of input
  const loss = amount - delivered;            // 3% of input
  return {
    delivered,
    loss
  };
};

export const applyGridLoss = (amount, efficiency) => {
  const delivered = amount * efficiency;      // 90% of input
  const loss = amount - delivered;            // 10% of input
  return {
    delivered,
    loss
  };
};